import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Button } from 'primeng/button';
import { BehaviorSubject, EMPTY, Observable, Subject, combineLatest, of, switchMap, take, takeUntil } from 'rxjs';
import { WebcamCaptureFormComponent, WebcamImageData } from '../../components/form-controls/webcam-capture-form/webcam-capture-form.component';
import { RemoteCaptureService, RemoteField, SessionStatus } from '../../services/remote-capture.service';

export interface MobileCaptureData {
  sessionId: string;
  computerId: string;
  fieldId: string;
  fileName: string;
  size: number;
  mimeType: string;
  base64Data: string;
  timestamp: Date;
}

@Component({
  selector: 'mobile-capture',
  templateUrl: './mobile-capture.component.html',
  styleUrls: ['./mobile-capture.component.scss'],
  standalone: true,
  imports: [CommonModule, Button, WebcamCaptureFormComponent]
})
export class MobileCaptureComponent implements OnInit, OnDestroy {
  computerId: string = '';
  sessionId: string = '';
  status: string = '';
  isCapturing: boolean = false;
  capturedImage: WebcamImageData | null = null; // Cambiado a WebcamImageData
  errorMessage: string = '';
  showWebcam = true;
  allowCameraSwitch = true;
  multipleWebcamsAvailable = false;
  remoteFields: RemoteField[] = [];
  selectedField: RemoteField | null = null;
  showFieldSelector: boolean = false;
  sessionValidated: boolean = false;

  // Propiedades para instrucciones
  showInstructions: boolean = false;

  // Propiedad para mensaje de validación
  validationMessage: string = '';
  showValidationMessage: boolean = false;
  
  // Flag para evitar procesar respuestas duplicadas
  private validationResponseProcessed: boolean = false;

  private destroy$ = new Subject<void>();
  private statusSubscription: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private remoteCaptureService: RemoteCaptureService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.computerId = this.route.snapshot.params['computerId'];
    this.sessionId = this.computerId;

    if (!this.computerId) {
      this.errorMessage = this.translateService.instant('MobileCapture.ErrorCampoNoEncontrado');
      this.status = this.translateService.instant('MobileCapture.ErrorComputerIdNoEncontrado');
      return;
    }

    this.initializeReactiveFlow();
  }

    private initializeReactiveFlow() {
    // Limpiar estado al inicializar
        this.sessionValidated = false;
        this.showWebcam = false;
    this.remoteFields = [];
    this.status = this.translateService.instant('MobileCapture.EsperandoSesion');
        this.errorMessage = '';

    // 1. Observable de estado de conexión WebSocket
    const connectionStatus$ = this.remoteCaptureService.getConnectionStatusObservable();

    // 2. Observable de estado de sesión
    const sessionStatus$ = connectionStatus$.pipe(
      switchMap(isConnected => {
        if (!isConnected) {
          return of({ active: false, message: this.translateService.instant('MobileCapture.DesconectadoServidor') });
        }

        const statusObservable = this.remoteCaptureService.getSessionStatusObservable(this.sessionId);

        if (statusObservable) {
          return statusObservable;
        } else {
          // Crear observable por defecto si no existe
          return new BehaviorSubject<SessionStatus>({
            active: false,
            message: this.translateService.instant('MobileCapture.EsperandoSesion'),
            sessionId: this.sessionId
          }).asObservable();
        }
      })
    );

    // 3. Observable de respuestas de validación
    const validationResponse$ = this.remoteCaptureService.getValidationResponseObservable();

    // 4. Combinar todos los observables para actualizar UI
    combineLatest([connectionStatus$, sessionStatus$, validationResponse$]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(([isConnected, sessionStatus, validationResponse]) => {
      this.updateUIState(isConnected, sessionStatus);

      // Manejar respuesta de validación si existe
      if (validationResponse) {
        this.handleValidationResponse(validationResponse);
      }
    });
  }

  private setupSessionRetryMechanism() {

    // ESCUCHAR CONSTANTEMENTE los cambios de estado de sesión
    // Esto asegura que el móvil detecte inmediatamente cuando el escritorio crea/termina sesiones
    this.remoteCaptureService.getSessionStatusObservable(this.sessionId)?.pipe(
      takeUntil(this.destroy$)
    ).subscribe((sessionStatus) => {
      if (sessionStatus) {
        if (sessionStatus.active && sessionStatus.fields && sessionStatus.fields.length > 0) {
          this.remoteFields = sessionStatus.fields;
          this.sessionValidated = true;
          this.cdr.detectChanges();
        } else if (!sessionStatus.active) {
          this.sessionValidated = false;
          this.remoteFields = [];
          this.cdr.detectChanges();
        }
      }
    });

        // NO verificar localStorage - solo confiar en WebSocket
  }

     private updateUIState(isConnected: boolean, sessionStatus: SessionStatus) {
    if (!isConnected) {
      this.status = this.translateService.instant('MobileCapture.ConectandoServidor');
      this.sessionValidated = false;
      this.showWebcam = false;
      this.remoteFields = [];
      this.errorMessage = ''; // No mostrar error mientras se conecta
      return;
    }

    if (!sessionStatus) {
      this.status = this.translateService.instant('MobileCapture.EsperandoEstadoSesion');
      this.sessionValidated = false;
      this.showWebcam = false;
      this.remoteFields = [];
      this.errorMessage = ''; // No mostrar error mientras espera
      return;
    }

    if (!sessionStatus.active) {
      this.status = sessionStatus.message || this.translateService.instant('MobileCapture.EsperandoSesion');
      this.sessionValidated = false;
      this.showWebcam = false;
      this.remoteFields = [];
      this.selectedField = null;
      this.capturedImage = null;
      this.showFieldSelector = false;
      this.showValidationMessage = false;
      this.validationMessage = '';

      // Mostrar mensaje de error solo si la sesión fue terminada
      if (sessionStatus.message && sessionStatus.message.includes('terminada')) {
        this.errorMessage = sessionStatus.message;
      } else {
        this.errorMessage = ''; // No mostrar error mientras espera sesión
      }
      return; // Importante: retornar aquí para no continuar
    }

    // Sesión activa
    if (sessionStatus.fields && sessionStatus.fields.length > 0) {
      this.loadRemoteFieldsFromSession(sessionStatus.fields);
      this.status = this.translateService.instant('MobileCapture.SesionValida');
          this.sessionValidated = true;
      this.errorMessage = '';
        } else {
      this.status = this.translateService.instant('MobileCapture.EsperandoCamposSesion');
      this.sessionValidated = false;
      this.showWebcam = false;
      this.remoteFields = [];
    }
  }

  private handleValidationResponse(validationResponse: any) {
    // Evitar procesar respuestas duplicadas
    if (this.validationResponseProcessed) {
      return;
    }
    
    // Marcar que se ha procesado una respuesta
    this.validationResponseProcessed = true;
    
    if (validationResponse.success) {
      // Validación exitosa
      this.status = validationResponse.message || this.translateService.instant('MobileCapture.ArchivoProcesadoExitosamente');

      // NO limpiar errorMessage si la sesión está terminada
      if (!this.errorMessage?.includes('terminada')) {
        this.errorMessage = '';
      }

      // Mostrar mensaje de validación exitosa
      this.validationMessage = validationResponse.message || this.translateService.instant('MobileCapture.ArchivoProcesadoExitosamente');
      this.showValidationMessage = true;

      // Comportamiento diferente según el número de campos
      if (this.remoteFields.length === 1) {
        // Un solo campo: reset automático para permitir nueva captura
        this.retakePhoto();
      } else {
        // Múltiples campos: reset pero mantener el campo seleccionado
        this.retakePhotoForMultipleFields();
      }
    } else {
      // Validación fallida
      this.status = this.translateService.instant('MobileCapture.ValidacionFallida');

      // NO limpiar errorMessage si la sesión está terminada
      if (!this.errorMessage?.includes('terminada')) {
        this.errorMessage = '';
      }

      // Mostrar mensaje de validación fallida
      this.validationMessage = validationResponse.message || this.translateService.instant('MobileCapture.ArchivoNoCumpleValidaciones');
      this.showValidationMessage = true;
      
      // Forzar detección de cambios para asegurar que el mensaje se muestre
      this.cdr.detectChanges();

      // NO limpiar la imagen - mantener para que usuario pueda corregir
      // this.capturedImage = null; // NO limpiar
      
      // SÍ resetear isCapturing para permitir que el usuario vea la imagen y pueda corregir
      this.isCapturing = false;

      // NO usar timer - el mensaje se mantiene visible hasta que el usuario tome nueva foto
    }
  }

    // Métodos obsoletos eliminados - ahora usamos flujo reactivo puro

  private loadRemoteFieldsFromSession(fields: RemoteField[]) {
    this.remoteFields = fields;
    this.capturedImage = null;
    this.isCapturing = false;
    this.showValidationMessage = false;
    this.validationMessage = '';

    if (this.remoteFields.length === 1) {
      this.selectedField = this.remoteFields[0];
      this.showFieldSelector = false;
      this.status = this.translateService.instant('MobileCapture.ListoParaCapturar');
      this.showWebcam = true;
    } else {
      this.selectedField = null;
      this.showFieldSelector = true;
      this.showWebcam = false;
    }
  }

  private loadRemoteFields() {
    // Simular carga de campos remotos (fallback)
    // En producción, esto vendría del backend
    this.remoteFields = [
      {
        id: 'field_0',
        label: this.translateService.instant('MobileCapture.FotosProducto'),
        description: this.translateService.instant('MobileCapture.ImagenesProductoDiferentesAngulos'),
        accept: 'image/*',
        multiple: true,
        maxFiles: 5,
        maxSize: 26214400, // 25MB para fotos de alta calidad
        required: true
      },
      {
        id: 'field_1',
        label: this.translateService.instant('MobileCapture.Documentos'),
        description: this.translateService.instant('MobileCapture.PDFsYDocumentosRelacionados'),
        accept: '.pdf,.doc,.docx',
        multiple: false,
        maxFiles: 1,
        maxSize: 26214400, // 25MB para documentos
        required: false
      },
      {
        id: 'field_2',
        label: this.translateService.instant('MobileCapture.FotoIdentificacion'),
        description: this.translateService.instant('MobileCapture.FotoDocumentoIdentidad'),
        accept: 'image/*',
        multiple: false,
        maxFiles: 1,
        maxSize: 26214400, // 25MB para fotos de alta calidad
        required: true
      }
    ];

    // Si solo hay un campo, seleccionarlo automáticamente
    if (this.remoteFields.length === 1) {
      this.selectedField = this.remoteFields[0];
      this.status = this.translateService.instant('MobileCapture.ListoParaCapturar');
    } else {
      this.showFieldSelector = true;
    }
  }

  // Método obsoleto eliminado - ahora usamos eventos WebSocket reactivos

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
    }
  }

  private checkMultipleWebcams() {
    // Verificar múltiples cámaras disponibles
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      navigator.mediaDevices.enumerateDevices()
        .then(devices => {
          const videoDevices = devices.filter(device => device.kind === 'videoinput');
          this.multipleWebcamsAvailable = videoDevices.length > 1;
        })
        .catch(error => {
          this.multipleWebcamsAvailable = false;
        });
    } else {
      this.multipleWebcamsAvailable = false;
    }
  }

  public triggerSnapshot(): void {
    // This method is no longer needed as WebcamCaptureFormComponent handles its own snapshot
  }

  public toggleWebcam(): void {
    this.showWebcam = !this.showWebcam;
  }

  public handleInitError(error: any): void {
    let errorMessage = this.translateService.instant('MobileCapture.ErrorInicializarCamara');

    if (error.message) {
      if (error.message.includes('Permission denied')) {
        errorMessage = this.translateService.instant('MobileCapture.PermisoDenegadoAccesoCamara');
      } else if (error.message.includes('NotAllowedError')) {
        errorMessage = this.translateService.instant('MobileCapture.AccesoCamaraDenegado');
      } else if (error.message.includes('NotFoundError')) {
        errorMessage = this.translateService.instant('MobileCapture.CamaraNoEncontrada');
      } else if (error.message.includes('NotReadableError')) {
        errorMessage = this.translateService.instant('MobileCapture.CamaraEnUsoOtroAplicativo');
      } else if (error.message.includes('OverconstrainedError')) {
        errorMessage = this.translateService.instant('MobileCapture.CamaraNoCumpleRequisitos');
      } else if (error.message.includes('SecurityError')) {
        errorMessage = this.translateService.instant('MobileCapture.ErrorSeguridadDesarrollo');
      } else {
        errorMessage = `Error: ${error.message}`;
      }
    }

    this.errorMessage = errorMessage;
    this.status = this.translateService.instant('MobileCapture.ErrorCamara');

    // Mostrar ayuda específica para desarrollo
    if (error.message && error.message.includes('SecurityError')) {
      this.errorMessage += ' 💡 ' + this.translateService.instant('MobileCapture.AyudaDesarrollo');
    }
  }

  public handleImage(webcamImage: WebcamImageData): void {
    this.capturedImage = webcamImage;
    this.isCapturing = false; // Cambiar a false para permitir envío
    this.status = this.translateService.instant('MobileCapture.ImagenCapturadaPresionarEnviarImagen');
    
    // Resetear flag de respuesta procesada para nueva imagen
    this.validationResponseProcessed = false;
  }

  public cameraWasSwitched(deviceId: string): void {
    // Cámara cambiada
  }

  public restartCamera(): void {
    this.showWebcam = false;
    this.errorMessage = '';
    this.status = this.translateService.instant('MobileCapture.ReiniciandoCamara');

    // Usar nextTick para evitar problemas de renderizado sin delays temporales
    Promise.resolve().then(() => {
      this.showWebcam = true;
      this.status = this.translateService.instant('MobileCapture.ListoParaCapturar');
      this.checkMultipleWebcams();
    });
  }

  public get triggerObservable(): Observable<void> {
    // This method is no longer needed as WebcamCaptureFormComponent handles its own snapshot
    return EMPTY;
  }

  public get nextWebcamObservable(): Observable<boolean | string> {
    // This method is no longer needed as WebcamCaptureFormComponent handles its own snapshot
    return EMPTY;
  }

  selectField(field: RemoteField) {
    this.selectedField = field;
    this.showFieldSelector = false;
    this.showWebcam = true; // Asegurar que la cámara esté visible
    this.status = this.translateService.instant('MobileCapture.ListoParaCapturar', { fieldLabel: field.label });

    // Limpiar mensaje de validación cuando se selecciona nuevo campo
    this.showValidationMessage = false;
    this.validationMessage = '';

    // Usar nextTick para verificar cámaras después del cambio de estado
    Promise.resolve().then(() => {
      this.checkMultipleWebcams();
    });
  }

  changeField() {
    this.selectedField = null;
    this.showFieldSelector = true;
    this.capturedImage = null;
    this.status = this.translateService.instant('MobileCapture.SeleccionarCampoCapturar');
    
    // Limpiar mensajes de validación cuando se cambia de campo
    this.showValidationMessage = false;
    this.validationMessage = '';
    this.errorMessage = '';
  }

  sendImage() {
    if (!this.capturedImage || !this.selectedField) {
      this.errorMessage = this.translateService.instant('MobileCapture.NoHayImagenCampoSeleccionado');
      this.status = this.translateService.instant('MobileCapture.ErrorDatosIncompletos');
      return;
    }

    // Verificar si el campo acepta imágenes
    const acceptTypes = this.selectedField.accept.split(',').map(type => type.trim());
    let acceptsImages = false;

    for (let acceptType of acceptTypes) {
      if (acceptType.includes('image') || acceptType.includes('*') || acceptType === '.jpg' || acceptType === '.jpeg' || acceptType === '.png') {
        acceptsImages = true;
        break;
      }
    }

    if (!acceptsImages) {
      const errorMsg = this.translateService.instant('MobileCapture.CampoNoAceptaImagenes', { fieldAccept: this.selectedField.accept });
      this.errorMessage = errorMsg;
      this.status = this.translateService.instant('MobileCapture.ImagenRechazadaTipoNoPermitido');
      return;
    }

    // Verificar tamaño de la imagen
    const imageSize = this.capturedImage.size; // Usar size directamente de WebcamImageData
    if (this.selectedField.maxSize && imageSize > this.selectedField.maxSize) {
      const errorMsg = this.translateService.instant('MobileCapture.ImagenDemasiadoGrande', {
        size: this.formatBytes(imageSize),
        maxSize: this.formatBytes(this.selectedField.maxSize)
      });
      this.errorMessage = errorMsg;
      this.status = this.translateService.instant('MobileCapture.ImagenRechazadaTamañoExcedeLimite');
      return;
    }

    // Limpiar mensajes de error si todo está bien
    this.errorMessage = '';
    this.status = this.translateService.instant('MobileCapture.EnviandoImagen');
    this.isCapturing = true;
    
    // Resetear flag de respuesta procesada para permitir nueva validación
    this.validationResponseProcessed = false;

    const captureData: MobileCaptureData = {
      sessionId: this.sessionId,
      computerId: this.computerId,
      fieldId: this.selectedField.id,
      fileName: this.capturedImage.fileName,
      size: imageSize,
      mimeType: this.capturedImage.mimeType,
      base64Data: this.capturedImage.base64Data, // Usar base64Data directamente
      timestamp: this.capturedImage.timestamp
    };

    // Enviar imagen a través de WebSocket
    this.sendImageViaWebSocket(captureData);
  }

  private getBase64Size(base64String: string): number {
    const stringLength = base64String.length;
    const sizeInBytes = 4 * Math.ceil(stringLength / 3) * 0.75;
    return Math.round(sizeInBytes);
  }

  private sendImageViaWebSocket(captureData: MobileCaptureData) {
    // Enviar inmediatamente al servicio WebSocket
      this.remoteCaptureService.sendFile(this.sessionId, captureData);

    // El mensaje de validación vendrá a través de handleValidationResponse
  }

  retakePhoto() {
    this.capturedImage = null;
    this.isCapturing = false;
    this.status = this.translateService.instant('MobileCapture.ListoParaCapturar');
    this.errorMessage = '';
    this.showWebcam = true; // Asegurar que la cámara se muestre nuevamente

    // Limpiar mensaje de validación cuando se toma nueva foto
    this.showValidationMessage = false;
    this.validationMessage = '';
  }

  retakePhotoForMultipleFields() {
    // Para múltiples campos: reset de la imagen pero mantener el campo seleccionado
    this.capturedImage = null;
    this.isCapturing = false;
    
    // Mantener el campo seleccionado y mostrar estado apropiado
    if (this.selectedField) {
      this.status = this.translateService.instant('MobileCapture.ListoParaCapturar', { fieldLabel: this.selectedField.label });
    } else {
      this.status = this.translateService.instant('MobileCapture.ListoParaCapturar');
    }
    
    this.errorMessage = '';
    this.showWebcam = true; // Asegurar que la cámara se muestre nuevamente

    // Solo limpiar mensaje de validación si no hay un error activo
    // Esto evita que se limpie el mensaje cuando hay un error de validación
    if (!this.showValidationMessage || !this.validationMessage.includes('excedió')) {
      this.showValidationMessage = false;
      this.validationMessage = '';
    }
  }

  switchCamera() {
    // This method is no longer needed as WebcamCaptureFormComponent handles its own snapshot
  }

  // Métodos para selección de archivos
  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;

  selectFile() {
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.click();
    }
  }

  onFileSelected(event: any) {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      this.processSelectedFile(file);
    }
  }

  private processSelectedFile(file: File) {
    // Verificar si el archivo es válido para el campo seleccionado
    if (!this.selectedField) {
      this.errorMessage = this.translateService.instant('MobileCapture.NoHayCampoSeleccionado');
      this.status = this.translateService.instant('MobileCapture.ErrorCampoNoSeleccionado');
      return;
    }

    // Verificar tipo de archivo
    const acceptTypes = this.selectedField.accept.split(',').map(type => type.trim());
    let isValidType = false;

    for (let acceptType of acceptTypes) {
      if (acceptType.includes('*')) {
        // Patrón con asterisco (ej: image/*)
        let baseType = acceptType.split('*')[0];
        if (file.type.startsWith(baseType)) {
          isValidType = true;
          break;
        }
      } else if (acceptType.startsWith('.')) {
        // Extensión (ej: .pdf)
        let extension = acceptType.toLowerCase();
        if (file.name.toLowerCase().endsWith(extension)) {
          isValidType = true;
          break;
        }
      } else {
        // MIME type completo
        if (file.type === acceptType) {
          isValidType = true;
          break;
        }
      }
    }

    if (!isValidType) {
      const errorMsg = this.translateService.instant('MobileCapture.TipoArchivoNoPermitido', {
        fileName: file.name,
        fieldAccept: this.selectedField.accept
      });
      this.errorMessage = errorMsg;
      this.status = this.translateService.instant('MobileCapture.ArchivoRechazadoTipoNoValido');
      return;
    }

    // Verificar tamaño
    if (this.selectedField.maxSize && file.size > this.selectedField.maxSize) {
      const errorMsg = this.translateService.instant('MobileCapture.ArchivoDemasiadoGrande', {
        fileName: file.name,
        size: this.formatBytes(file.size),
        maxSize: this.formatBytes(this.selectedField.maxSize)
      });
      this.errorMessage = errorMsg;
      this.status = this.translateService.instant('MobileCapture.ArchivoRechazadoTamañoExcedeLimite');
      return;
    }

    // Limpiar mensajes de error si todo está bien
    this.errorMessage = '';
    this.status = this.translateService.instant('MobileCapture.ProcesandoArchivo');

    // Convertir archivo a base64
    this.convertFileToBase64(file);
  }

  private convertFileToBase64(file: File) {
    this.status = this.translateService.instant('MobileCapture.ProcesandoArchivo');
    this.isCapturing = true;

    const reader = new FileReader();
    reader.onload = (e: any) => {
      const base64 = e.target.result.split(',')[1]; // Remover el prefijo data:...
      const mimeType = file.type || this.getMimeTypeFromExtension(file.name);

      // Crear un objeto similar a WebcamImageData para archivos de galería
      const fileImageData: WebcamImageData = {
        base64Data: e.target.result,
        fileName: file.name,
        mimeType: mimeType,
        size: file.size,
        timestamp: new Date()
      };

      // Establecer capturedImage para archivos de galería también
      this.capturedImage = fileImageData;
      this.isCapturing = false; // Cambiar a false para permitir mostrar mensajes de validación
      this.status = this.translateService.instant('MobileCapture.ImagenCapturadaPresionarEnviarImagen');
      
      // Resetear flag de respuesta procesada para nuevo archivo
      this.validationResponseProcessed = false;

      const fileData: MobileCaptureData = {
        sessionId: this.sessionId,
        computerId: this.computerId,
        fieldId: this.selectedField!.id,
        fileName: file.name,
        size: file.size,
        mimeType: mimeType,
        base64Data: e.target.result, // Incluir el prefijo completo
        timestamp: new Date()
      };

      // Enviar archivo
      this.sendFileData(fileData);
    };

    reader.onerror = () => {
      this.errorMessage = this.translateService.instant('MobileCapture.ErrorProcesarArchivo');
      this.isCapturing = false;
      this.status = this.translateService.instant('MobileCapture.ErrorProcesarArchivo');
    };

    reader.readAsDataURL(file);
  }

  private getMimeTypeFromExtension(filename: string): string {
    const extension = filename.toLowerCase().split('.').pop();
    const mimeTypes: { [key: string]: string } = {
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'txt': 'text/plain'
    };
    return mimeTypes[extension || ''] || 'application/octet-stream';
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private sendFileData(fileData: MobileCaptureData) {
    // Enviar inmediatamente al servicio WebSocket
      this.remoteCaptureService.sendFile(this.sessionId, fileData);

    // Limpiar input inmediatamente
      if (this.fileInput && this.fileInput.nativeElement) {
        this.fileInput.nativeElement.value = '';
      }

    // El mensaje de validación vendrá a través de handleValidationResponse
  }

  // Métodos para tamaño responsivo de la cámara
  getCameraWidth(): number {
    const screenWidth = window.innerWidth;
    const isMobile = screenWidth <= 768;

    if (isMobile) {
      // En móviles, usar ancho completo menos padding y márgenes
      const padding = 32; // 16px * 2 (izquierda y derecha)
      const margins = 16; // 8px * 2 (márgenes del contenedor)
      return screenWidth - padding - margins;
    }

    // En desktop, usar un ancho máximo
    return Math.min(500, screenWidth - 64);
  }

  getCameraHeight(): number {
    const screenWidth = window.innerWidth;
    const isMobile = screenWidth <= 768;

    if (isMobile) {
      // En móviles, mantener aspect ratio 4:3 pero asegurar que quepa
      const width = this.getCameraWidth();
      const height = Math.round(width * 0.75);

      // Verificar que la altura no exceda el 60% de la altura de la pantalla
      const maxHeight = window.innerHeight * 0.6;
      return Math.min(height, maxHeight);
    }

    // En desktop, mantener aspect ratio 4:3
    const width = this.getCameraWidth();
    return Math.round(width * 0.75);
  }

  getVideoOptions(): any {
    const width = this.getCameraWidth();
    const height = this.getCameraHeight();

    return {
      width: { ideal: width, max: 4096 }, // Permitir resolución más alta
      height: { ideal: height, max: 4096 },
      facingMode: 'environment', // Usar cámara trasera por defecto
      aspectRatio: { ideal: 4/3 },
      frameRate: { ideal: 30, max: 60 } // Permitir frame rate más alto
    };
  }

  // Método para toggle de instrucciones
  toggleInstructions(): void {
    this.showInstructions = !this.showInstructions;
  }

  // Método de debugging - mostrar información del sistema
  debugInfo(): void {
    // Estado de localStorage
    const currentStatus = this.remoteCaptureService.getCurrentSessionStatus(this.sessionId);

    // Estado de conexión WebSocket
    this.remoteCaptureService.getConnectionStatusObservable().pipe(take(1)).subscribe((isConnected: boolean) => {
      // WebSocket Connected
    });
  }

  // Método de prueba - crear sesión de prueba
  createTestSession(): void {
    const testFields: RemoteField[] = [
      {
        id: 'test_field',
        label: this.translateService.instant('MobileCapture.CampoPrueba'),
        description: this.translateService.instant('MobileCapture.CampoPruebaConnexionMovil'),
        accept: 'image/*',
        multiple: false,
        maxFiles: 1,
        maxSize: 26214400, // 25MB para fotos de alta calidad
        required: false
      }
    ];

    this.remoteCaptureService.createSession(this.sessionId, testFields);
  }

  closeWindow() {
    // Intentar cerrar la ventana
    if (window.opener) {
      window.close();
    } else {
      // Si no se puede cerrar, mostrar mensaje
      this.status = this.translateService.instant('MobileCapture.PuedeCerrarVentana');
    }
  }

  goBack() {
    // Navegar de vuelta o cerrar
    if (window.history.length > 1) {
      window.history.back();
    } else {
      this.closeWindow();
    }
  }
}
